<template>
  <div>
    <b-card>
      <div class="d-flex flex-wrap align-items-start justify-content-between mb-2">
        <div>
          <h5 class="font-weight-bold mb-1">
            <i class="fas fa-user-md mr-2"></i>
            Enrollments by OMA
          </h5>
          <p class="text-muted small mb-0">
            Patients enrolled per OMA (first enrollment). Historical enrollments without OMA
            recorded are not counted until backfill.
          </p>
        </div>
        <b-button
          variant="outline-success"
          size="sm"
          class="mt-2 mt-md-0"
          :disabled="loading || !items.length || exporting"
          @click="exportCsv">
          <i class="fas fa-file-csv mr-1"></i>
          {{ exporting ? 'Exporting…' : 'Export CSV' }}
        </b-button>
      </div>

      <b-alert v-if="forbidden" show variant="warning" class="mb-3">
        This report is only available for admin and superadmin users.
      </b-alert>

      <b-row v-else class="wl-filters m-1 mb-1 align-items-start">
        <b-col cols="12" md="3">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.facilities_id"
              :options="facilityOptions"
              :normalizer="normalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="facilityOptionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="From (enrolled_at)" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input v-model="filters.date_from" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="To (enrolled_at)" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input v-model="filters.date_to" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3" class="d-flex justify-content-end align-items-end pb-1">
          <b-button
            variant="outline-primary"
            class="wl-filter-btn mt-2 mt-md-0"
            :disabled="facilityOptionsLoading || loading"
            @click="load">
            Filter
          </b-button>
        </b-col>
      </b-row>

      <b-table
        v-if="!forbidden"
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No enrollments found"
        :busy="loading">
        <template #cell(oma)="data">
          <strong>{{ omaName(data.item) }}</strong>
          <small class="text-muted d-block">#{{ data.item.oma_id }}</small>
        </template>
        <template #cell(enrolled_count)="data">
          {{ Number(data.value) || 0 }}
        </template>
        <template #table-busy>
          <div class="text-center text-muted my-3">
            <b-spinner class="align-middle mr-2" />
            Loading…
          </div>
        </template>
      </b-table>

      <p v-if="!forbidden && items.length" class="text-muted small mb-0 mt-2">
        Total enrolled (sum): <strong>{{ totalEnrolled }}</strong>
      </p>
    </b-card>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BFormGroup,
  BFormInput,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import ToastNotification from '@/components/ToastNotification'
import { buildListQueryParams } from '@/core/services/api/weightloss-inventory/helpers'
import axiosF from '@/core/services/api/admin/facilities'
import wlLaneMixin from '../mixins/wlLaneMixin'
import { isWeightlossReportAdmin } from '../utils/wlRoles'
import { exportRowsToCsv, csvFilename } from '../utils/exportCsv'

export default {
  name: 'ReportEnrollmentsByOma',
  mixins: [wlLaneMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BFormGroup,
    BFormInput,
    BAlert,
    BSpinner,
    Treeselect,
    ToastNotification,
  },
  data() {
    return {
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
      loading: false,
      exporting: false,
      forbidden: false,
      items: [],
      facilityOptions: [],
      facilityOptionsLoading: false,
      filters: {
        facilities_id: [],
        date_from: '',
        date_to: '',
      },
      fields: [
        { key: 'oma', label: 'OMA' },
        { key: 'enrolled_count', label: 'Enrolled patients' },
      ],
    }
  },
  computed: {
    totalEnrolled() {
      return this.items.reduce((sum, row) => sum + (Number(row.enrolled_count) || 0), 0)
    },
  },
  created() {
    if (!isWeightlossReportAdmin()) {
      this.forbidden = true
      return
    }
    this.loadFacilities()
    this.load()
  },
  methods: {
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch {
        this.facilityOptions = []
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    buildParams() {
      return buildListQueryParams({
        facilities_id: this.filters.facilities_id,
        date_from: this.filters.date_from || null,
        date_to: this.filters.date_to || null,
      })
    },
    omaName(item) {
      if (!item) return '—'
      const full = `${item.name || ''} ${item.lastname || ''}`.trim()
      return full || (item.oma_id != null ? `#${item.oma_id}` : '—')
    },
    async load() {
      this.loading = true
      this.forbidden = false
      try {
        const data = await this.wlReportsApi.enrollmentsByOma(this.buildParams())
        this.items = Array.isArray(data) ? data : []
      } catch (e) {
        this.items = []
        if (e && e.status === 403) {
          this.forbidden = true
          return
        }
        if (this.$refs.toast) {
          this.$refs.toast.danger((e && e.message) || 'Could not load enrollments by OMA')
        }
      } finally {
        this.loading = false
      }
    },
    exportCsv() {
      this.exporting = true
      try {
        exportRowsToCsv(
          this.items,
          [
            { key: 'oma_id', label: 'OMA ID' },
            { key: 'name', label: 'Name' },
            { key: 'lastname', label: 'Last name' },
            { key: 'enrolled_count', label: 'Enrolled patients' },
          ],
          csvFilename('wl_enrollments_by_oma')
        )
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.wl-filter-btn {
  min-width: 7rem;
}
</style>
